/**
 * `NotificationSnapshot`, the record of one notification mutation carried in
 * clear inside a `notification.feed` envelope (TRD section 3). Validated by
 * the producer before publish and by the rendering client on receipt.
 *
 * The payload reuses the feed's own `NotificationItem`, so a notification
 * rendered from a snapshot is byte-for-byte what `GET /api/notifications`
 * would have returned. There is no per-kind content policy and no field
 * filter: every kind travels inline, as the feed route would serve it (TRD
 * Draft 6). The `withheld` variant stays as an escape hatch a producer may
 * choose per publish; nothing selects it automatically.
 */
import { z } from "zod";
import { DismissAllNotificationsResponse, NotificationItem } from "../notifications.js";
import { RelayResourceId, RelayTimestamp } from "./primitives.js";
/** Bumped when the snapshot's shape changes, so a client can refuse a shape it does not know. */
export const NOTIFICATION_SNAPSHOT_PAYLOAD_VERSION = 1;
/** A new or changed notification: the full feed item, or only its id. */
function upserted(mutation) {
    return [
        z.strictObject({ mutation: z.literal(mutation), notification: NotificationItem }),
        // Content stays in Postgres; the client refetches GET /api/notifications.
        z.strictObject({
            mutation: z.literal(mutation),
            notificationId: RelayResourceId,
            withheld: z.literal(true),
        }),
    ];
}
export const NotificationSnapshot = z.union([
    ...upserted("created"),
    ...upserted("updated"),
    z.strictObject({
        mutation: z.literal("dismissed"),
        notificationId: RelayResourceId,
        dismissedAt: RelayTimestamp,
    }),
    // Applying it twice is a no-op.
    z.strictObject({
        mutation: z.literal("dismissed_all"),
        dismissed: DismissAllNotificationsResponse.shape.dismissed,
        dismissedAt: RelayTimestamp,
    }),
]);
//# sourceMappingURL=snapshot.js.map